import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import { StateValues } from 'react-use-form-state';
import { AdminApi, DistributionPlatform, InvestorEntryType } from 'api';
import useApiCall from 'hooks/use-api-call';
import Translate from 'ui/atoms/translate';
import { Modal, ModalHeader, ModalContent, ModalFooter, ModalFooterButtons, makeModalForm } from 'ui/molecules/modal';
import * as Styled from './styled';
import { PersonType } from 'ui/types/person';
import { AdminInvestorCreationRequest } from 'api/models/AdminInvestorCreationRequest';
import Icon from 'src/ui/atoms/icon';
import Spacer from 'src/ui/atoms/spacer';
import Hint from 'src/ui/atoms/hint';
import Header from 'src/ui/atoms/header';
import Section from 'src/ui/atoms/section';

interface AddInvestorModalProps {
  onAddNewInvestor: () => void;
  onClose: () => void;
  distributionPlatforms: DistributionPlatform[];
}

interface AddInvestorFields {
  distributionPlatformId: string;
  personType: PersonType;
  entryType: InvestorEntryType;
  firstName: string;
  companyName?: string;
  forename: string;
  surname: string;
  email: string;
  pepStatus: boolean;
  isBeneficiary: boolean;
  isSubjectToUsTax: boolean;
}

export const Form = makeModalForm<AddInvestorFields>();

const AddInvestorModal: FunctionComponent<AddInvestorModalProps> = ({
  onAddNewInvestor,
  onClose,
  distributionPlatforms,
}) => {
  const [selectedDistributionPlatformId, setSelectedDistributionPlatformId] = useState<string | undefined>(
    distributionPlatforms?.length === 1 ? distributionPlatforms[0].id : undefined,
  );
  const [allowSingleEntry, setAllowSingleEntry] = useState(false);
  const [allowCumulativeEntry, setAllowCumulativeEntry] = useState(false);
  const [selectedEntryType, setSelectedEntryType] = useState<InvestorEntryType>(InvestorEntryType.SINGLE_ENTRY);
  const [submitted, setSubmitted] = useState(false);

  const { makeAuthenticatedApi, withApi } = useApiCall();

  const api: AdminApi = useMemo(() => makeAuthenticatedApi(AdminApi), [makeAuthenticatedApi]);
  const [isPepStatusExtended, setPepStatusExtended] = useState(false);

  const onClickExtendPepDescription = useCallback(
    (evt: any) => {
      evt.preventDefault();
      setPepStatusExtended(!isPepStatusExtended);
    },
    [isPepStatusExtended],
  );

  const fetchDistributionPlatform = useCallback(() => {
    if (!selectedDistributionPlatformId) return;

    withApi(async () => {
      try {
        const { allowSingleEntry, allowCumulativeEntry } = await api.adminDistributionPlatformsRetrieve({
          id: selectedDistributionPlatformId,
        });
        setAllowSingleEntry(allowSingleEntry);
        setAllowCumulativeEntry(allowCumulativeEntry);
      } catch (e) {
        console.error(e);
      }
    });
  }, [api, selectedDistributionPlatformId]);

  useEffect(() => {
    if (!selectedDistributionPlatformId) return;

    setSubmitted(false);
    fetchDistributionPlatform();
  }, [selectedDistributionPlatformId]);

  useEffect(() => {
    setSelectedEntryType(
      !allowSingleEntry && allowCumulativeEntry ? InvestorEntryType.CUMULATIVE_ENTRY : InvestorEntryType.SINGLE_ENTRY,
    );
  }, [allowSingleEntry, allowCumulativeEntry]);

  const onSubmit = useCallback(
    (fieldsValues: StateValues<AddInvestorFields>) => {
      setSubmitted(true);
      if (!allowSingleEntry && !allowCumulativeEntry) return;

      const params: AdminInvestorCreationRequest = {
        distributionPlatformId: fieldsValues.distributionPlatformId,
        forename: fieldsValues.forename,
        surname: fieldsValues.surname,
        email: fieldsValues.email,
        isPep: !fieldsValues.pepStatus,
        isBeneficiary: fieldsValues.isBeneficiary,
        isSubjectToUsTax: !fieldsValues.isSubjectToUsTax,
        entryType: selectedEntryType,
      };

      if (fieldsValues.personType === PersonType.Legal) {
        params.companyName = fieldsValues.companyName;
      }

      withApi(async () => {
        await api.adminInvestorsCreate({ adminInvestorCreationRequest: params });
        onAddNewInvestor();
      });
    },
    [allowSingleEntry, allowCumulativeEntry, selectedEntryType],
  );

  return (
    <Modal onClose={onClose} size="large">
      <Form
        i18nKey="addInvestor"
        onSubmit={(values: StateValues<AddInvestorFields>) => {
          onSubmit(values);
        }}
        initial={{
          personType: PersonType.Natural,
          entryType: InvestorEntryType.SINGLE_ENTRY,
          distributionPlatformId: selectedDistributionPlatformId,
        }}
      >
        <ModalHeader>
          <Translate name="addInvestor.title" />
        </ModalHeader>
        <ModalContent>
          <Form.ValueProvider>
            {(values) => (
              <>
                {!!distributionPlatforms?.length && (
                  <Section spacing="regular">
                    <Form.Group name="distributionPlatformId" required={true}>
                      <Form.Select
                        options={distributionPlatforms.map(({ id, displayName }) => ({
                          value: id,
                          label: displayName,
                        }))}
                        onChange={(distributionPlatformId: string) => {
                          setSelectedDistributionPlatformId(distributionPlatformId);
                        }}
                      ></Form.Select>
                    </Form.Group>
                  </Section>
                )}

                <Form.Group name="personType" labelSpacing="none" required={true}>
                  <Form.GroupToggle>
                    {[PersonType.Natural, PersonType.Legal].map((personType, index) => (
                      <Form.Radio value={personType} key={index}>
                        <Translate name={`personTypes.${personType}`} />
                      </Form.Radio>
                    ))}
                  </Form.GroupToggle>
                </Form.Group>
                <Form.Group name="entryType" labelSpacing="none" required={true}>
                  <Form.GroupToggle>
                    <Form.Radio
                      value={InvestorEntryType.SINGLE_ENTRY}
                      key="0"
                      disabled={!allowSingleEntry}
                      checked={selectedEntryType === InvestorEntryType.SINGLE_ENTRY}
                      onChange={() => setSelectedEntryType(InvestorEntryType.SINGLE_ENTRY)}
                    >
                      <Translate name="entryType.single" />
                    </Form.Radio>
                    <Form.Radio
                      value={InvestorEntryType.CUMULATIVE_ENTRY}
                      key="1"
                      disabled={!allowCumulativeEntry || values.personType === PersonType.Natural}
                      checked={selectedEntryType === InvestorEntryType.CUMULATIVE_ENTRY}
                      onChange={() => setSelectedEntryType(InvestorEntryType.CUMULATIVE_ENTRY)}
                    >
                      <Translate name="entryType.cumulative" />
                    </Form.Radio>
                  </Form.GroupToggle>
                </Form.Group>
                {values.personType === PersonType.Legal && (
                  <Form.Group name="companyName" required={true}>
                    <Form.Input />
                  </Form.Group>
                )}
                <Styled.NameContainer>
                  <Form.Group name="forename" required={true}>
                    <Form.Input />
                  </Form.Group>
                  <Spacer x={4} />
                  <Form.Group name="surname" required={true}>
                    <Form.Input />
                  </Form.Group>
                </Styled.NameContainer>
                <Section spacing="regular">
                  <Form.Group name="email" required={true}>
                    <Form.Input />
                    {Form.Validators.Email()}
                  </Form.Group>
                </Section>

                <Section spacing="regular">
                  <Header spacing="medium">
                    <Translate name="addInvestor.checkboxesHeading" />
                  </Header>
                  <Form.Group name="pepStatus" required={true}>
                    <Form.Checkbox>
                      <Translate name="addInvestor.pepStatus" />
                      <Spacer inline x={2} />
                      <Icon name="question" size="regular" onClick={(evt: any) => onClickExtendPepDescription(evt)} />
                      {isPepStatusExtended && (
                        <Hint variant="info">
                          <Translate name="addInvestor.pepStatusExtended" />
                        </Hint>
                      )}
                    </Form.Checkbox>
                  </Form.Group>
                  <Form.Group name="isBeneficiary" required={true}>
                    <Form.Checkbox>
                      <Translate name="addInvestor.isBeneficiary" />
                    </Form.Checkbox>
                  </Form.Group>
                  <Form.Group name="isSubjectToUsTax" required={true}>
                    <Form.Checkbox>
                      <Translate
                        name={`createAccount.accept.${
                          values.personType === PersonType.Natural ? 'usTaxNaturalPerson' : 'usTaxLegalPerson'
                        }`}
                      />
                    </Form.Checkbox>
                  </Form.Group>
                </Section>

                <Translate name="addInvestor.furtherData" as="p" />

                {submitted && !allowSingleEntry && !allowCumulativeEntry && (
                  <Hint variant="danger">
                    <Translate name="addInvestor.notAllowedError" />
                  </Hint>
                )}
              </>
            )}
          </Form.ValueProvider>
        </ModalContent>
        <ModalFooter>
          <ModalFooterButtons
            actionButtons={[
              {
                name: 'cancel',
                content: <Translate name="common.cancel" />,
                size: 'large',
                onClick: () => onClose(),
              },
              {
                name: 'send',
                content: <Translate name="common.add" />,
                onClick: () => setSubmitted(true),
                variant: 'primary',
                type: 'submit',
                size: 'large',
              },
            ]}
          />
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default AddInvestorModal;
